export type PanelCounts = {
  required?: number;
  recommended?: number;
  sex_specific_required?: number;
  sex_specific_recommended?: number;
};

export type ModeSummary = {
  mode_id: string;
  display_name: string;
  category: string;
  description: string;
  panel_counts: PanelCounts;
  has_clinical_modifiers: boolean;
  reference_range_overrides_count: number;
  indicated_compounds_count: number;
  indicated_monitoring_count: number;
  monitoring_max?: number | null;
  contraindication_profile_count: number;
  dose_modifications_count: number;
  synthesiser_addendum_present: boolean;
  critic_addenda_drift_types: string[];
};

export type ModesResponse = {
  version: number | string | null;
  last_updated: string | null;
  modes: ModeSummary[];
  warnings: string[];
};

// Category pill colours + labels — verbatim from the legacy admin-modes.html.
export const MODE_CATEGORY_HEX: Record<string, string> = {
  user_pursued: '#5B72E8',
  clinical_state: '#F2635A',
};

export function filterModes(list: ModeSummary[], query: string): ModeSummary[] {
  const q = query.toLowerCase().trim();
  if (!q) return list;
  return list.filter((m) =>
    m.mode_id.toLowerCase().includes(q) ||
    m.display_name.toLowerCase().includes(q) ||
    (m.description || '').toLowerCase().includes(q));
}

// ---- Mode detail (/api/modes/{id}) — the full authored entry ----

export type ClinicalModifiers = {
  reference_range_overrides?: unknown[];
  indicated_compounds?: Array<Record<string, unknown> | string>;
  indicated_monitoring?: unknown[];
  contraindication_profile?: unknown[];
  dose_modifications?: unknown[];
  tier_3_candidates?: unknown[];
};

export type SubBranch = {
  display_name?: string;
  interpretive_lens?: string;
  clinical_modifiers?: ClinicalModifiers;
  synthesiser_addendum?: string;
  critic_addenda?: unknown;
};

export type ModePanels = {
  required?: string[];
  recommended?: string[];
  sex_specific_required?: Record<string, string[]>;
  sex_specific_recommended?: Record<string, string[]>;
};

export type ModeEntry = {
  display_name?: string;
  category?: string;
  description?: string;
  interpretive_lens?: string;
  panels?: ModePanels;
  clinical_modifiers?: ClinicalModifiers;
  sub_branches?: Record<string, SubBranch>;
  active_sub_branch?: string;
  tasks?: { required?: string[]; recommended?: string[]; include_universal?: boolean };
  synthesiser_addendum?: string;
  critic_addenda?: unknown;
};

function compoundKey(c: Record<string, unknown> | string): string {
  if (typeof c === 'string') return c.toLowerCase();
  return String(c.compound ?? c.name ?? JSON.stringify(c)).toLowerCase();
}

/** All clinical_modifiers blocks for a mode: the top-level + every sub-branch. */
export function allModifiers(mode: ModeEntry): ClinicalModifiers[] {
  const out: ClinicalModifiers[] = [];
  if (mode.clinical_modifiers) out.push(mode.clinical_modifiers);
  for (const sb of Object.values(mode.sub_branches ?? {})) if (sb.clinical_modifiers) out.push(sb.clinical_modifiers);
  return out;
}

export type ModeLinkCounts = {
  panelsRequired: number;
  panelsRecommended: number;
  panelsSexSpecific: number;
  compounds: number;
  contraindications: number;
  monitoring: number;
  doseMods: number;
  subBranches: number;
  tasksRequired: number;
  tasksRecommended: number;
};

/** Counts for the registries-index cards on the mode detail page. */
export function modeLinkCounts(mode: ModeEntry): ModeLinkCounts {
  const cms = allModifiers(mode);
  const sumKey = (k: keyof ClinicalModifiers) => cms.reduce((n, cm) => n + ((cm[k] as unknown[])?.length ?? 0), 0);

  const compounds = new Set<string>();
  for (const cm of cms) for (const c of cm.indicated_compounds ?? []) compounds.add(compoundKey(c));
  const contra = new Set<string>();
  for (const cm of cms) for (const c of cm.contraindication_profile ?? []) contra.add(JSON.stringify(c));

  const p = mode.panels ?? {};
  const sexCount = (block?: Record<string, string[]>) => Object.values(block ?? {}).reduce((n, arr) => n + (arr?.length ?? 0), 0);

  return {
    panelsRequired: (p.required ?? []).length,
    panelsRecommended: (p.recommended ?? []).length,
    panelsSexSpecific: sexCount(p.sex_specific_required) + sexCount(p.sex_specific_recommended),
    compounds: compounds.size,
    contraindications: contra.size,
    monitoring: sumKey('indicated_monitoring'),
    doseMods: sumKey('dose_modifications'),
    subBranches: Object.keys(mode.sub_branches ?? {}).length,
    tasksRequired: (mode.tasks?.required ?? []).length,
    tasksRecommended: (mode.tasks?.recommended ?? []).length,
  };
}

export type Contraindication = {
  source: string;              // 'mode' or a sub_branch id
  canonical_compound_id?: string;
  applies_when?: Record<string, unknown>;
  applies_when_prose?: string;
  severity?: string;
  rationale?: string;
  evidence_basis?: string;
};

/** Every contraindication a mode carries, across the top-level + each sub-branch. */
export function modeContraindications(mode: ModeEntry): Contraindication[] {
  const out: Contraindication[] = [];
  const collect = (source: string, cm?: ClinicalModifiers) => {
    for (const c of (cm?.contraindication_profile ?? []) as Contraindication[]) {
      if (c && typeof c === 'object') out.push({ ...c, source });
    }
  };
  collect('mode', mode.clinical_modifiers);
  for (const [sbId, sb] of Object.entries(mode.sub_branches ?? {})) collect(sbId, sb.clinical_modifiers);
  return out;
}

/** The panel ids a mode references, flattened across tiers (deduped, order-preserving). */
export function modePanelIds(mode: ModeEntry): { id: string; tier: 'required' | 'recommended'; sex?: string }[] {
  const out: { id: string; tier: 'required' | 'recommended'; sex?: string }[] = [];
  const seen = new Set<string>();
  const push = (id: string, tier: 'required' | 'recommended', sex?: string) => {
    const k = `${id}:${sex ?? ''}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ id, tier, sex });
  };
  const p = mode.panels ?? {};
  for (const id of p.required ?? []) push(id, 'required');
  for (const id of p.recommended ?? []) push(id, 'recommended');
  for (const [sex, ids] of Object.entries(p.sex_specific_required ?? {})) for (const id of ids) push(id, 'required', sex);
  for (const [sex, ids] of Object.entries(p.sex_specific_recommended ?? {})) for (const id of ids) push(id, 'recommended', sex);
  return out;
}
